import { RefObject } from "react";
import createImageIdsAndCacheMetaData from "./createImageIdsAndCacheMetaData";
import createNiftiImageIds from "./createNiftiImageIds";
import readNiftiVoxels from "./helpers/readNiftiVoxels";
import initCornerstone from "./initCornerstone";
import {
  RenderingEngine,
  volumeLoader,
  setVolumesForViewports,
  cache,
  getRenderingEngine,
  imageLoadPoolManager,
  ImageVolume,
  utilities as csUtils,
} from "@cornerstonejs/core";
import {
  StackScrollTool,
  ToolGroupManager,
  CrosshairsTool,
  Enums as csToolsEnums,
  segmentation as cstSegmentation,
  WindowLevelTool,
  PanTool,
  ZoomTool,
  BrushTool,
  utilities,
} from "@cornerstonejs/tools";
import {
  SegmentationIds,
  RenderingEngineIds,
  ViewerIds,
} from "../types/cornerstone";
import {
  PublicViewportInput,
  IRenderingEngine,
} from "@cornerstonejs/core/dist/esm/types";
import * as cornerstoneTools from "@cornerstonejs/tools";
import getIds from "./helpers/getIds";
import { brushInstanceNames, brushStrategies } from "./helpers/toolsName";
import {
  getSegInfo,
  injectBufferIntoSegVolume,
  SegInfo,
} from "./generateSegmentation";
import viewerState, { isNiftiMetadata, type Segment } from "stores/viewer";
import settingsState, {
  updateWindowLevel,
  updateRoiOpacity,
} from "stores/settings";
import dcmjs from "dcmjs";
import roiSummary from "./commons/roiSummary.json";

const { MouseBindings, KeyboardBindings } = cornerstoneTools.Enums;

export async function initViewer(
  id: string,
  viewerRefs: RefObject<HTMLDivElement>[],
  instanceMetaData: dcmjs.data.NaturalizedDataset,
  notify: (message: string, options?: object) => void,
) {
  if (!viewerState.instanceMetadata) return;
  viewerState.viewerId = id;

  const rawMeta = viewerState.instanceMetadata;
  const isNifti = isNiftiMetadata(rawMeta);
  const niftiUrl = isNifti ? rawMeta._niftiUrl : undefined;
  const niftiSegUrl = isNifti ? rawMeta._niftiSegUrl : undefined;
  const niftiSegUrls = isNifti ? rawMeta._niftiSegUrls : undefined;

  const {
    renderingEngineId,
    viewportInputConf,
    volumeId,
    segVolumeId,
    segmentationId,
    toolGroupId,
    viewportId1,
    viewportId2,
    viewportId3,
  } = getIds();

  const renderingEngine = await setupRenderingEngine(viewerRefs, id, {
    renderingEngineId,
    viewportInputConf,
  });

  if (isNifti && niftiUrl) {
    const niftiVolumeId = `nifti:${niftiUrl}`;

    await loadNiftiVolume(
      {
        volumeId: niftiVolumeId,
        viewportId1,
        viewportId2,
        viewportId3,
      },
      niftiUrl,
      renderingEngine,
    );

    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    if (toolGroup) {
      toolGroup.setToolActive(CrosshairsTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
    }

    renderingEngine.render();

    if (niftiSegUrls) {
      const { segments, datasets } = buildNiftiSegmentsFromRoiSummary(
        niftiSegUrls,
        segmentationId,
      );

      viewerState.segmentsPerSeries = segments;
      viewerState.niftiDatasets = datasets;
    }

    const segmentationPromise = (async () => {
      const bases = (viewerState.niftiBases ?? {}) as Record<string, string>;

      if (niftiSegUrls) {
        let idx = 0;
        for (const [setType, segUrl] of Object.entries(niftiSegUrls)) {
          const modality = BV_TYPE_TO_MODALITY[setType] ?? "t1mri";
          const baseUrl = bases[modality] ?? niftiUrl;
          await loadNiftiSegmentation(segUrl, {
            segmentationId: `${segmentationId}_nifti_${idx}`,
            baseVolumeId: `nifti:${baseUrl}`,
          });
          idx++;
        }
      } else if (niftiSegUrl) {
        await loadNiftiSegmentation(niftiSegUrl, {
          segmentationId: `${segmentationId}_nifti_0`,
          baseVolumeId: niftiVolumeId,
        });
      }
    })();

    segmentationPromise.catch((err) => {
      console.error("Failed to load NIfTI segmentations", err);
    });

    return renderingEngine;
  }

  const [imageIds, getSEGPixelData] = await createImageIdsAndCacheMetaData({
    StudyInstanceUID: instanceMetaData.StudyInstanceUID!,
    SeriesInstanceUID: instanceMetaData.SeriesInstanceUID!,
  });
  const segInfo = getSEGPixelData?.().then((SEGArrayBuffer) =>
    //@ts-ignore
    getSegInfo(imageIds, SEGArrayBuffer),
  );

  const volume = await createAndLoadVolume(
    { volumeId, viewportId1, viewportId2, viewportId3 },
    imageIds,
    renderingEngine,
  );
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (toolGroup) {
    toolGroup.setToolActive(CrosshairsTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });
  }

  loadFramesMiddleOutwards(
    volume,
    { volumeId, viewportId1, viewportId2, viewportId3 },
    renderingEngine,
  );

  await setupSegmentation(
    {
      volumeId: volume.volumeId,
      segVolumeId,
      segmentationId,
      toolGroupId,
    },
    segInfo,
    notify,
  );
  return renderingEngine;
}
async function loadNiftiVolume(
  viewerIds: ViewerIds,
  niftiUrl: string,
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  let volume = cache.getVolume(volumeId) as ImageVolume | undefined;
  if (!volume) {
    const imageIds = await createNiftiImageIds(niftiUrl);
    volume = await volumeLoader.createAndCacheVolume(volumeId, { imageIds });
  }
  await volume.load();

  try {
    const [spX, spY, spZ] = volume.spacing;

    if (spX !== spY) {
      const correctedSpacing: [number, number, number] = [spY, spX, spZ];
      volume.spacing = correctedSpacing;

      const vtkImageData = (volume as any).imageData;
      if (vtkImageData?.setSpacing) {
        vtkImageData.setSpacing(correctedSpacing);
        vtkImageData.modified();
      }
    }
  } catch (e) {
    console.warn("[NIfTI] Failed to correct volume spacing:", e);
  }
  // ═══════════════════════════════════════════════════════════════════════════════

  await setVolumesForViewports(renderingEngine, [{ volumeId }], viewportIds);

  renderingEngine.resize(true, true);

  for (const vpId of viewportIds) {
    const vp = renderingEngine.getViewport(vpId) as any;
    vp.resetCamera();
  }

  jumpViewportsToCenterOfVolume(renderingEngine, viewportIds, volume);

  for (const vpId of viewportIds) {
    renderingEngine.getViewport(vpId).render();
  }

  try {
    const vp = renderingEngine.getViewport(viewportId1) as any;
    const props = vp?.getProperties?.();
    if (props?.voiRange) {
      const { lower, upper } = props.voiRange;
      const { windowWidth, windowCenter } = csUtils.windowLevel.toWindowLevel(
        lower,
        upper,
      );
      updateWindowLevel(
        { width: Math.round(windowWidth), center: Math.round(windowCenter) },
        true,
      );
    }
  } catch (e) {}

  return volume;
}
function jumpViewportsToCenterOfVolume(
  renderingEngine: IRenderingEngine,
  viewportIds: string[],
  volume: ImageVolume,
) {
  try {
    const { dimensions, imageData } = volume as any;
    if (!dimensions || !imageData?.indexToWorld) return;

    const centerIndex: [number, number, number] = [
      Math.floor(dimensions[0] / 2),
      Math.floor(dimensions[1] / 2),
      Math.floor(dimensions[2] / 2),
    ];

    const centerWorld = imageData.indexToWorld(centerIndex) as [
      number,
      number,
      number,
    ];

    for (const vpId of viewportIds) {
      try {
        const viewport = renderingEngine.getViewport(vpId) as any;
        const camera = viewport?.getCamera();
        if (camera?.focalPoint && camera?.position) {
          const delta = [
            centerWorld[0] - camera.focalPoint[0],
            centerWorld[1] - camera.focalPoint[1],
            centerWorld[2] - camera.focalPoint[2],
          ];
          viewport.setCamera({
            focalPoint: centerWorld,
            position: [
              camera.position[0] + delta[0],
              camera.position[1] + delta[1],
              camera.position[2] + delta[2],
            ],
          });
        }
      } catch {
        // ignore per-viewport errors
      }
    }
  } catch {
    // ignore if volume metadata is not yet available
  }
}
export async function switchNiftiBase(newBaseUrl: string) {
  const { renderingEngineId, viewportId1, viewportId2, viewportId3 } = getIds(
    viewerState.viewerId,
  );

  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) return;

  const niftiVolumeId = `nifti:${newBaseUrl}`;
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  const imageIds = await createNiftiImageIds(newBaseUrl);
  let volume = cache.getVolume(niftiVolumeId) as ImageVolume | undefined;
  if (!volume) {
    volume = await volumeLoader.createAndCacheVolume(niftiVolumeId, {
      imageIds,
    });
  }
  await volume.load();

  try {
    const [spX, spY, spZ] = volume.spacing;
    if (spX !== spY) {
      const correctedSpacing: [number, number, number] = [spY, spX, spZ];
      volume.spacing = correctedSpacing;

      const vtkImageData = (volume as any).imageData;
      if (vtkImageData?.setSpacing) {
        vtkImageData.setSpacing(correctedSpacing);
        vtkImageData.modified();
      }
    }
  } catch (e) {
    console.warn("[switchNiftiBase] Failed to correct volume spacing:", e);
  }

  await setVolumesForViewports(
    renderingEngine,
    [{ volumeId: niftiVolumeId }],
    viewportIds,
  );

  for (const vpId of viewportIds) {
    const vp = renderingEngine.getViewport(vpId) as any;
    vp?.resetCamera?.();
  }

  jumpViewportsToCenterOfVolume(renderingEngine, viewportIds, volume);

  try {
    const vp = renderingEngine.getViewport(viewportId1) as any;
    const props = vp?.getProperties?.();
    if (props?.voiRange) {
      const { lower, upper } = props.voiRange;
      const { windowWidth, windowCenter } = csUtils.windowLevel.toWindowLevel(
        lower,
        upper,
      );
      updateWindowLevel(
        { width: Math.round(windowWidth), center: Math.round(windowCenter) },
        true,
      );
    }
  } catch (e) {}

  renderingEngine.render();
}

async function loadNiftiSegmentation(
  niftiSegUrl: string,
  {
    segmentationId,
    baseVolumeId,
  }: { segmentationId: string; baseVolumeId: string },
) {
  const segId = segmentationId;
  const derivedId = `${segId}__labelmap`;

  if (cstSegmentation.state.getSegmentation(segId)) return;

  const segVoxels = await readNiftiVoxels(niftiSegUrl);
  const srcScalars = segVoxels.data;

  let baseVolume = cache.getVolume(baseVolumeId) as ImageVolume | undefined;
  if (!baseVolume) {
    const baseUrl = baseVolumeId.replace(/^nifti:/, "");
    const baseImageIds = await createNiftiImageIds(baseUrl);
    baseVolume = await volumeLoader.createAndCacheVolume(baseVolumeId, {
      imageIds: baseImageIds,
    });
  }
  await baseVolume.load();

  let derived = cache.getVolume(derivedId) as ImageVolume | undefined;
  if (!derived) {
    derived = await volumeLoader.createAndCacheDerivedLabelmapVolume(
      baseVolumeId,
      { volumeId: derivedId },
    );
    await new Promise((resolve) => setTimeout(resolve, 0));
    derived = cache.getVolume(derivedId) as ImageVolume;
  }
  if (!derived) {
    console.error(`[nifti seg] failed to create derived labelmap ${derivedId}`);
    return;
  }

  const dims = derived.dimensions;
  const derivedVoxels = dims[0] * dims[1] * dims[2];
  if (srcScalars.length !== derivedVoxels) {
    console.warn(
      `[nifti seg] geometry mismatch, skipping ${segId}: ` +
        `src=${srcScalars.length} (${segVoxels.dimensions}) ` +
        `derived=${derivedVoxels} (${dims}) base=${baseVolumeId}`,
    );
    return;
  }
  if (!writeLabelsIntoVolume(derived, srcScalars)) {
    console.error(`[nifti seg] failed to write labels into ${derivedId}`);
    return;
  }

  cstSegmentation.addSegmentations([
    {
      segmentationId: segId,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Labelmap,
        data: { volumeId: derivedId },
      },
    },
  ]);
}

function writeLabelsIntoVolume(
  volume: ImageVolume,
  labels: ArrayLike<number>,
): boolean {
  const vm = (volume as any).voxelManager;

  if (vm?.setCompleteScalarDataArray) {
    try {
      vm.setCompleteScalarDataArray(Uint8Array.from(labels as any));
      vm.modified?.();
      return true;
    } catch (e) {
      console.warn("[nifti seg] setCompleteScalarDataArray failed:", e);
    }
  }

  if (vm) {
    try {
      const scalarData = vm.scalarData ?? vm.getCompleteScalarDataArray?.();
      if (scalarData && scalarData.length === labels.length) {
        for (let i = 0; i < labels.length; i++) scalarData[i] = labels[i];
        vm.modified?.();
        return true;
      }
    } catch (e) {
      console.warn("[nifti seg] voxelManager direct write failed:", e);
    }
  }

  try {
    const scalars = (volume as any).imageData?.getPointData?.()?.getScalars?.();
    const arr = scalars?.getData?.();
    if (arr && arr.length === labels.length) {
      for (let i = 0; i < labels.length; i++) arr[i] = labels[i];
      (volume as any).imageData.modified();
      return true;
    }
  } catch (e) {
    console.warn("[nifti seg] imageData write failed:", e);
  }

  return false;
}

const MODALITY_DISPLAY_NAMES: Record<string, string> = {
  t1mri: "T1 segmentation",
  t2flairmri: "WMH segmentation",
};

const BV_TYPE_TO_MODALITY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (node: any) => {
    if (node?.bv_type) map[node.bv_type] = node.modality ?? "t1mri";
    for (const c of node?.sub_rois ?? []) walk(c);
    for (const c of node?.lateral_rois ?? []) walk(c);
  };
  for (const group of Object.values(roiSummary)) walk(group);
  return map;
})();

const MODALITY_ORDER: Record<string, number> = {
  t1mri: 0,
  t2flairmri: 1,
};

function buildNiftiSegmentsFromRoiSummary(
  segUrls: Record<string, string>,
  baseSegId: string,
): { segments: Segment[]; datasets: any[] } {
  const segments: Segment[] = [];
  const setTypeToSegId = new Map<string, string>();
  let idx = 0;
  for (const setType of Object.keys(segUrls)) {
    setTypeToSegId.set(setType, `${baseSegId}_nifti_${idx}`);
    idx++;
  }

  const modalitiesWithSegments = new Set<string>();

  for (const [, group] of Object.entries(roiSummary)) {
    const g = group as any;
    if (!g.implemented || !g.bv_type) continue;

    const csSegmentationId = setTypeToSegId.get(g.bv_type);
    if (!csSegmentationId) continue;

    const modality: string = g.modality ?? "t1mri";
    const syntheticSeriesUID = `nifti:seg:${modality}`;

    modalitiesWithSegments.add(modality);

    collectRoiSegments(
      g,
      syntheticSeriesUID,
      csSegmentationId,
      segments,
      g.color,
      setTypeToSegId,
    );
  }

  const datasets: any[] = [];
  for (const modality of modalitiesWithSegments) {
    datasets.push({
      SeriesInstanceUID: `nifti:seg:${modality}`,
      Modality: "SEG",
      SeriesDescription:
        MODALITY_DISPLAY_NAMES[modality] ?? `${modality} segmentation`,
      _order: MODALITY_ORDER[modality] ?? 99,
    });
  }

  return { segments, datasets };
}

function collectRoiSegments(
  roi: any,
  seriesUID: string,
  segmentationId: string,
  segments: Segment[],
  parentColor?: number[],
  setTypeToSegId?: Map<string, string>,
) {
  const effectiveColor = roi.color ?? parentColor;
  const effectiveSegId =
    (roi.bv_type && setTypeToSegId?.get(roi.bv_type)) ?? segmentationId;

  if (roi.label != null && roi.code) {
    const codeParts = roi.code.split("-");

    segments.push({
      SegmentNumber: roi.label,
      SegmentLabel: roi.name ?? roi.code,
      SegmentedPropertyTypeCodeSequence: [
        {
          CodeValue: codeParts[0],
          ...(codeParts.length > 1
            ? {
                SegmentedPropertyTypeModifierCodeSequence: [
                  { CodeValue: codeParts[1] },
                ],
              }
            : {}),
        },
      ],
      RecommendedDisplayCIELabValue: undefined,
      _rgbColor: effectiveColor,
      selected: false,
      representationUID: effectiveSegId,
      seriesUID,
    } as any);
  }

  for (const child of roi.sub_rois ?? []) {
    collectRoiSegments(
      child,
      seriesUID,
      effectiveSegId,
      segments,
      effectiveColor,
      setTypeToSegId,
    );
  }
  for (const child of roi.lateral_rois ?? []) {
    collectRoiSegments(
      child,
      seriesUID,
      effectiveSegId,
      segments,
      effectiveColor,
      setTypeToSegId,
    );
  }
}

function generateRequests(customOrderedRequests: any[], volumeId: string) {
  const requestType = "prefetch";
  const priority = 0;
  return customOrderedRequests.map(
    ({ callLoadImage, imageId, imageIdIndex, options }) => {
      const additionalDetails = { volumeId: volumeId };
      return {
        callLoadImage: callLoadImage.bind(this, imageId, imageIdIndex, options),
        requestType,
        additionalDetails,
        priority,
      };
    },
  );
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function imageLoadPoolManagerRequestAsync(
  request: any,
  retries = 3,
  delay = 1000,
) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await new Promise<void>((resolve) => {
        imageLoadPoolManager.addRequest(
          request.callLoadImage,
          request.requestType,
          request.additionalDetails,
          request.priority,
        );
        resolve();
      });
      return;
    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error);
      if (attempt < retries) {
        await sleep(delay);
      } else {
        console.error("All attempts failed.");
        throw error;
      }
    }
  }
}

async function loadFramesMiddleOutwards(
  volume: any,
  viewerIds: ViewerIds,
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;
  const loadRequests = await volume.getImageLoadRequests();
  const totalFrames = loadRequests.length;
  const batchSize = 5;
  const middle = Math.floor(totalFrames / 2);
  const failedFrames = new Set<number>();
  const MAX_BATCH_RETRIES = 3;

  async function loadBatch(startIdx: number, endIdx: number, attempt = 0) {
    const batch = loadRequests.slice(startIdx, endIdx + 1);
    const requests = await generateRequests(batch, volumeId);

    try {
      await Promise.all(
        requests.map(async (request) => {
          try {
            await imageLoadPoolManagerRequestAsync(request);
          } catch (error) {
            failedFrames.add(startIdx + requests.indexOf(request));
            throw error;
          }
        }),
      );
    } catch (error) {
      if (attempt < MAX_BATCH_RETRIES) {
        console.warn(
          `Retrying batch ${startIdx}-${endIdx}, attempt ${attempt + 1}`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt)),
        );
        return loadBatch(startIdx, endIdx, attempt + 1);
      }
    }

    await Promise.all(
      [viewportId1, viewportId2, viewportId3].map(async (viewportId) => {
        const viewport = renderingEngine.getViewport(viewportId);
        viewport.render();
      }),
    );
  }

  for (
    let i = middle, j = middle + 1;
    i >= 0 || j < totalFrames;
    i -= batchSize, j += batchSize
  ) {
    const leftStart = Math.max(i - batchSize + 1, 0);
    const rightEnd = Math.min(j + batchSize - 1, totalFrames - 1);

    await Promise.all([loadBatch(leftStart, i), loadBatch(j, rightEnd)]);
  }
  if (failedFrames.size > 0) {
    console.warn(`Retrying ${failedFrames.size} failed frames individually`);
    await Promise.all(
      Array.from(failedFrames).map(async (frameIndex) => {
        const request = (
          await generateRequests([loadRequests[frameIndex]], volumeId)
        )[0];
        return imageLoadPoolManagerRequestAsync(request, 5, 2000);
      }),
    );
  }
}

async function createAndLoadVolume(
  viewerIds: ViewerIds,
  imageIds: string[],
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;

  let volume = cache.getVolume(volumeId) as ImageVolume | undefined;
  if (!volume) {
    volume = await volumeLoader.createAndCacheVolume(volumeId, {
      imageIds,
    });
  }

  await setVolumesForViewports(
    renderingEngine,
    [{ volumeId }],
    [viewportId1, viewportId2, viewportId3],
  );

  for (const vpId of [viewportId1, viewportId2, viewportId3]) {
    const vp = renderingEngine.getViewport(vpId) as any;
    vp.render();
  }

  try {
    const vp = renderingEngine.getViewport(viewportId1) as any;
    const props = vp?.getProperties?.();
    if (props?.voiRange) {
      const { lower, upper } = props.voiRange;
      const { windowWidth, windowCenter } = csUtils.windowLevel.toWindowLevel(
        lower,
        upper,
      );
      updateWindowLevel(
        { width: Math.round(windowWidth), center: Math.round(windowCenter) },
        true,
      );
    }
  } catch {}

  return volume;
}

async function setupRenderingEngine(
  viewerRefs: RefObject<HTMLDivElement>[],
  id: string,
  { renderingEngineId, viewportInputConf }: RenderingEngineIds,
) {
  let renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) {
    await initCornerstone();
    renderingEngine = new RenderingEngine(renderingEngineId);
  }
  const viewportInputs: PublicViewportInput[] = viewportInputConf.map(
    (v, i) => ({
      ...v,
      element: viewerRefs[i].current!,
    }),
  );
  renderingEngine.setViewports(viewportInputs);

  setupTools(id);
  return renderingEngine;
}

function setupTools(id: string) {
  const {
    renderingEngineId,
    toolGroupId,
    voiSynchronizerId,
    viewportId1,
    viewportId2,
    viewportId3,
    getReferenceLineColor,
    getReferenceLineControllable,
    getReferenceLineDraggableRotatable,
    getReferenceLineSlabThicknessControlsOn,
  } = getIds(id);

  const toolGroup =
    ToolGroupManager.getToolGroup(toolGroupId) ||
    ToolGroupManager.createToolGroup(toolGroupId);

  toolGroup.addViewport(viewportId1, renderingEngineId);
  toolGroup.addViewport(viewportId2, renderingEngineId);
  toolGroup.addViewport(viewportId3, renderingEngineId);

  if (!toolGroup._toolInstances[StackScrollTool.toolName]) {
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.setToolActive(StackScrollTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Wheel }],
    });
  }

  if (!toolGroup._toolInstances[WindowLevelTool.toolName]) {
    toolGroup.addTool(WindowLevelTool.toolName);
  }

  if (!toolGroup._toolInstances[PanTool.toolName]) {
    toolGroup.addTool(PanTool.toolName);
  }

  if (!toolGroup._toolInstances[ZoomTool.toolName]) {
    toolGroup.addTool(ZoomTool.toolName);
  }

  if (!toolGroup._toolInstances[BrushTool.toolName]) {
    toolGroup.addTool(BrushTool.toolName);
    toolGroup.addToolInstance(
      brushInstanceNames.SphereBrush,
      BrushTool.toolName,
      {
        activeStrategy: brushStrategies[brushInstanceNames.SphereBrush],
      },
    );
    toolGroup.addToolInstance(
      brushInstanceNames.SphereEraser,
      BrushTool.toolName,
      {
        activeStrategy: brushStrategies[brushInstanceNames.SphereEraser],
      },
    );
  }

  const voiSynchronizer =
    cornerstoneTools.SynchronizerManager.getSynchronizer(voiSynchronizerId) ||
    cornerstoneTools.synchronizers.createVOISynchronizer(voiSynchronizerId, {
      syncInvertState: true,
      syncColormap: true,
    });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId1 });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId2 });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId3 });

  if (!toolGroup._toolInstances[CrosshairsTool.toolName]) {
    toolGroup.addTool(CrosshairsTool.toolName, {
      getReferenceLineColor,
      getReferenceLineControllable,
      getReferenceLineDraggableRotatable,
      getReferenceLineSlabThicknessControlsOn,
    });
  }

  toolGroup.setToolActive(CrosshairsTool.toolName);

  toolGroup.setToolActive(WindowLevelTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });

  toolGroup.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });

  toolGroup.setToolActive(ZoomTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Auxiliary,
        modifierKey: KeyboardBindings.Ctrl,
      },
    ],
  });

  toolGroup.setToolActive(StackScrollTool.toolName);
  const { SegmentationRepresentations } = csToolsEnums;

  const initialOpacity = settingsState.roiOpacity ?? 100;
  const alphaValue = initialOpacity / 100;
  updateRoiOpacity(initialOpacity, true); // setDefault = true

  cstSegmentation.config.style.setStyle(
    {
      type: SegmentationRepresentations.Labelmap,
    },
    {
      renderOutline: false,
      outlineWidth: 0,
      outlineWidthInactive: 0,
      renderOutlineInactive: false,
      fillAlpha: alphaValue,
      fillAlphaInactive: alphaValue,
    },
  );
  utilities.segmentation.setBrushSizeForToolGroup(toolGroupId, 5);
}

async function setupSegmentation(
  { volumeId, segVolumeId, segmentationId, toolGroupId }: SegmentationIds,
  segInfoPromise: Promise<SegInfo>,
  notify: (message: string, options?: object) => void,
) {
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (!toolGroup) return;

  const viewportIds = toolGroup.viewportsInfo.map((v) => v.viewportId);

  const anyViewportHasSeg = viewportIds.some((vpId) => {
    const reps =
      cstSegmentation.state.getSegmentationRepresentations(vpId) || [];
    return reps.some((rep: any) =>
      String(rep.segmentationId).includes(segmentationId),
    );
  });

  if (anyViewportHasSeg || !segInfoPromise) return;

  const segInfo = await segInfoPromise;
  const labelMapBuffers = segInfo.labelmapBufferArray;

  if (!labelMapBuffers?.length) {
    console.error("SEG parsing didn't return any buffer");
    notify("notification.error.segmentation.parsing", { type: "warning" });
    return;
  }

  await Promise.all(
    labelMapBuffers.map(async (_b, i) => {
      const derivedId = `${segVolumeId}_${i}`;
      if (cache.getVolume(derivedId)) return;

      await volumeLoader.createAndCacheDerivedVolume(volumeId, {
        volumeId: derivedId,
        targetBuffer: { type: "Uint8Array" },
      });
      await new Promise((resolve) => setTimeout(resolve, 0));

      const cachedSegVolume = cache.getVolume(derivedId) as ImageVolume;

      if (cachedSegVolume) {
        injectBufferIntoSegVolume(cachedSegVolume, labelMapBuffers[i]);
      } else {
        console.error("Failed to get cached derived volume:", derivedId);
      }
    }),
  );

  const segmentations = labelMapBuffers
    .filter(
      (_b, i) =>
        !cstSegmentation.state.getSegmentation(`${segmentationId}_${i}`),
    )
    .map((_b, i) => ({
      segmentationId: `${segmentationId}_${i}`,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Labelmap,
        data: { volumeId: `${segVolumeId}_${i}` },
      },
    }));

  if (segmentations.length) {
    cstSegmentation.addSegmentations(segmentations);
  }

  const segmentationIds = labelMapBuffers.map(
    (_b, i) => `${segmentationId}_${i}`,
  );
  if (viewerState.instanceMetadata) {
    viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
      (segment) => {
        if (
          segment.seriesUID !== viewerState.instanceMetadata?.SeriesInstanceUID
        )
          return segment;

        const segmentIndex = segment.SegmentNumber;
        const bufferIndex = segInfo.segmentsOnFrameArray.findIndex((arr) =>
          arr.flat().includes(segmentIndex),
        );
        const representationUID =
          bufferIndex < 0 ? segmentationIds[0] : segmentationIds[bufferIndex];

        return { ...segment, representationUID, selected: false };
      },
    );
  }
}
